import React, { useState, useRef } from 'react';
import {
  VStack, FormControl, FormLabel, Input, Select, Textarea, Button,
  SimpleGrid, useToast, HStack, Text, Box, Icon, Badge,
} from '@chakra-ui/react';
import { FiUpload, FiFile, FiTrash2 } from 'react-icons/fi';
import axiosInstance from '../../../api/AxiosInstance';

interface Props {
  artista?: any;
  onGuardado: () => void;
}

const TIPOS = ['Individual', 'Grupo', 'Colectivo', 'Colaborador'];

const FormularioArtista: React.FC<Props> = ({ artista, onGuardado }) => {
  const toast = useToast();
  const docRef = useRef<HTMLInputElement>(null);
  const [guardando, setGuardando] = useState(false);
  const [nuevosDocumentos, setNuevosDocumentos] = useState<File[]>([]);

  const documentosExistentes = (artista?.archivos || []).filter((a: any) => a.tipoArchivo === 'documento');

  const [form, setForm] = useState({
    tipo: artista?.tipo || 'Individual',
    nombre: artista?.nombre || '',
    integrantes: artista?.integrantes || '',
    edad: artista?.edad || '',
    telefono: artista?.telefono || '',
    correo: artista?.correo || '',
    disciplina: artista?.disciplina || '',
    disponibilidad: artista?.disponibilidad || '',
    honorarios: artista?.honorarios || '',
    historial: artista?.historial || '',
    evaluacion: artista?.evaluacion || '',
  });

  const set = (f: string, v: string) => setForm((p) => ({ ...p, [f]: v }));

  const handleDocumentos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setNuevosDocumentos((p) => [...p, ...files].slice(0, 5));
    e.target.value = '';
  };

  const handleGuardar = async () => {
    if (!form.nombre || !form.tipo) {
      toast({ title: 'Nombre y tipo son obligatorios', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => formData.append(k, String(v)));
      nuevosDocumentos.forEach((f) => formData.append('documentos', f));

      if (artista?.id) {
        await axiosInstance.put(`/cultura/artistas/${artista.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast({ title: 'Actualizado correctamente', status: 'success', duration: 3000 });
      } else {
        await axiosInstance.post('/cultura/artistas', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast({ title: 'Registrado correctamente', status: 'success', duration: 3000 });
      }
      onGuardado();
    } catch (e: any) {
      toast({ title: 'Error', description: e?.response?.data?.message || 'Error al guardar', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Tipo</FormLabel>
          <Select value={form.tipo} onChange={(e) => set('tipo', e.target.value)}>
            {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Nombre / Nombre del grupo</FormLabel>
          <Input value={form.nombre} onChange={(e) => set('nombre', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Integrantes</FormLabel>
          <Input value={form.integrantes} onChange={(e) => set('integrantes', e.target.value)}
            placeholder="Nombres de integrantes (si aplica)" />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Edad / Rango de edades</FormLabel>
          <Input value={form.edad} onChange={(e) => set('edad', e.target.value)} placeholder="Ej. 25 años / 18-35 años" />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Teléfono de contacto</FormLabel>
          <Input value={form.telefono} onChange={(e) => set('telefono', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Correo electrónico</FormLabel>
          <Input type="email" value={form.correo} onChange={(e) => set('correo', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Disciplina artística</FormLabel>
          <Input value={form.disciplina} onChange={(e) => set('disciplina', e.target.value)}
            placeholder="Ej. Danza folclórica, Pintura, Canto" />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Honorarios estimados (MXN)</FormLabel>
          <Input type="number" min={0} value={form.honorarios} onChange={(e) => set('honorarios', e.target.value)}
            placeholder="$ MXN" />
        </FormControl>
      </SimpleGrid>

      <FormControl>
        <FormLabel fontSize="sm">Disponibilidad</FormLabel>
        <Textarea rows={2} value={form.disponibilidad} onChange={(e) => set('disponibilidad', e.target.value)}
          placeholder="Horarios, fechas disponibles, cobertura geográfica..." />
      </FormControl>

      <FormControl>
        <FormLabel fontSize="sm">Historial de participaciones</FormLabel>
        <Textarea rows={3} value={form.historial} onChange={(e) => set('historial', e.target.value)}
          placeholder="Eventos, festivales y presentaciones en los que ha participado..." />
      </FormControl>

      <FormControl>
        <FormLabel fontSize="sm">Evaluación / Notas</FormLabel>
        <Textarea rows={2} value={form.evaluacion} onChange={(e) => set('evaluacion', e.target.value)}
          placeholder="Observaciones, calificación, referencias..." />
      </FormControl>

      {/* Documentos */}
      <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
        <Text fontSize="sm" fontWeight="bold" mb={2} color="#5b182b">Documentos (hasta 5 archivos)</Text>
        {documentosExistentes.length > 0 && (
          <VStack align="start" spacing={1} mb={2}>
            {documentosExistentes.map((a: any) => (
              <HStack key={a.id}>
                <Icon as={FiFile} color="gray.500" />
                <Text fontSize="xs" color="gray.600">{a.nombreArchivo}</Text>
              </HStack>
            ))}
          </VStack>
        )}
        {nuevosDocumentos.length > 0 && (
          <VStack align="start" spacing={1} mb={2}>
            {nuevosDocumentos.map((f, i) => (
              <HStack key={i}>
                <Icon as={FiFile} color="green.500" />
                <Text fontSize="xs">{f.name}</Text>
                <Badge colorScheme="green">Nuevo</Badge>
                <Button size="xs" variant="ghost" colorScheme="red"
                  onClick={() => setNuevosDocumentos((p) => p.filter((_, j) => j !== i))}>
                  <Icon as={FiTrash2} />
                </Button>
              </HStack>
            ))}
          </VStack>
        )}
        <input ref={docRef} type="file" multiple accept=".pdf,image/*" style={{ display: 'none' }}
          onChange={handleDocumentos} />
        <Button size="xs" leftIcon={<Icon as={FiUpload} />} variant="outline"
          onClick={() => docRef.current?.click()}
          isDisabled={documentosExistentes.length + nuevosDocumentos.length >= 5}>
          Agregar documentos
        </Button>
      </Box>

      <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleGuardar}
        isLoading={guardando} loadingText="Guardando...">
        {artista?.id ? 'Actualizar registro' : 'Guardar registro'}
      </Button>
    </VStack>
  );
};

export default FormularioArtista;
